import paper from "paper";
import type {
  ConstructionGroup,
  ConstructionLine,
  ConstraintRef,
  Project,
  TracingImage,
  Vec2,
} from "./Project";
import { projectConstraints } from "./constraints";
import {
  descendantConstructionImageIds,
  descendantConstructionLineIds,
  projectConstructionGroups,
  projectConstructionLines,
  projectTracingImages,
  tracingImageCenter,
  tracingImageRotation,
} from "./Project";
import {
  rotatePrimitive,
  translateConstructionLine,
} from "../tools/SelectTool";
import { displayedBoxCenter, unrotatePoint } from "./transformHandles";

/** Normalise a degree value into the (-180, 180] half-open range so
 *  the rotation scrubber + chain math doesn't drift across full
 *  turns. 180 maps to itself; -180 maps to 180 (matches Math.atan2
 *  convention). */
export function normalizeRotation(deg: number): number {
  if (!Number.isFinite(deg)) return 0;
  let d = deg % 360;
  if (d > 180) d -= 360;
  if (d <= -180) d += 360;
  return d;
}

/** Compose two rotations (`r1` about `p1` followed by `r2` about
 *  `p2`) into a single equivalent rotation about a derived pivot.
 *  The composition of two 2-D rotations is itself a rotation
 *  (unless the angles sum to 0 mod 360, in which case the result is
 *  a pure translation and we return null so the caller can clear
 *  the rotation state).
 *
 *  Derivation: applying `R(p2, r2) ∘ R(p1, r1)` to a point x gives
 *  `R(r1+r2)·(x − p1) + R(r2)·(p1 − p2) + p2`. Setting this equal
 *  to `R(r1+r2)·(x − p_combined) + p_combined` and solving for
 *  `p_combined` gives the closed-form below.
 *
 *  Used to preserve the bbox-tilt invariant when a construction
 *  line is rotated about a non-bbox-centre pivot (issue #67
 *  follow-up): the previously stored rotation/pivot pair composes
 *  with the new rotation instead of being reset, so the dashed
 *  bbox stays the un-rotated size rotated by the new accumulated
 *  angle. */
export function composeRotations(
  r1Deg: number,
  p1: Vec2,
  r2Deg: number,
  p2: Vec2,
): { deg: number; center: Vec2 } | null {
  const totalDeg = r1Deg + r2Deg;
  const totalRad = (totalDeg * Math.PI) / 180;
  const cosT = Math.cos(totalRad);
  const sinT = Math.sin(totalRad);
  // M = I − R(total) = [[a, b], [−b, a]] where a = 1−cosT, b = sinT.
  // det(M) = a² + b² = 2(1 − cosT). Zero iff total rotation is 0
  // mod 360 — the composition collapses to a translation and no
  // single pivot exists.
  const a = 1 - cosT;
  const b = sinT;
  const det = a * a + b * b;
  if (det < 1e-9) return null;
  const r2Rad = (r2Deg * Math.PI) / 180;
  const cos2 = Math.cos(r2Rad);
  const sin2 = Math.sin(r2Rad);
  // RHS = (R(r2) − R(total))·p1 + (I − R(r2))·p2
  const rhsX =
    (cos2 - cosT) * p1.x -
    (sin2 - sinT) * p1.y +
    (1 - cos2) * p2.x +
    sin2 * p2.y;
  const rhsY =
    (sin2 - sinT) * p1.x +
    (cos2 - cosT) * p1.y -
    sin2 * p2.x +
    (1 - cos2) * p2.y;
  // p_combined = M^-1 · RHS where M^-1 = (1/det)·[[a, −b], [b, a]]
  const px = (a * rhsX - b * rhsY) / det;
  const py = (b * rhsX + a * rhsY) / det;
  return {
    deg: normalizeRotation(totalDeg),
    center: { x: px, y: py },
  };
}

/** Apply a rotation by `deg` about `(cx, cy)` to one construction
 *  line, returning the rotated copy. The line's `pathData` (and any
 *  rounded sharp-source) is baked in rotated form.
 *
 *  `options.trackRotation` (default true) controls whether the
 *  cumulative `rotation` / `rotationCenter` fields are updated:
 *  - true: the bbox-tilt invariant is maintained — used when the
 *    pivot is the line's natural bbox centre (top-handle or
 *    bbox-corner gestures).
 *  - false: the rotation field is reset to 0 and `rotationCenter`
 *    cleared. Used when the pivot is an arbitrary anchor; the
 *    bbox-tilt invariant can't survive a pivot change, so we treat
 *    the rotated geometry as the new natural frame.
 *
 *  No-ops if `deg` is effectively zero — keeps history clean when a
 *  scrubber drag releases at the exact same value it started at. */
export function rotateConstructionLine(
  line: ConstructionLine,
  deg: number,
  cx: number,
  cy: number,
  scope: paper.PaperScope,
  options: { trackRotation?: boolean } = {},
): ConstructionLine {
  if (Math.abs(deg) < 1e-6) return line;
  const trackRotation = options.trackRotation !== false;
  const next: ConstructionLine = {
    ...line,
    pathData: rotatePathData(line.pathData, cx, cy, deg, scope),
  };
  // Primitive preservation (issue #168). Rotation no longer drops the
  // primitive metadata; instead it follows the primitive's own
  // parameterisation:
  //  - rectangle / ellipse — axis-aligned bbox; orientation is tracked
  //    on the line via `rotation` / `rotationCenter`, so the primitive
  //    object itself stays unchanged.
  //  - line / pill — endpoints encode orientation directly; the
  //    endpoints are rotated to match. For these the redundant
  //    `rotation` field is cleared below.
  next.primitive = rotatePrimitive(line.primitive, deg, cx, cy);
  // `convertedFromPrimitive` is the snapshot of the line's natural
  // primitive form (set when a reversible bezier-cycle drops the
  // primitive). A rigid rotation is reversible too, so transform the
  // snapshot in parallel rather than discarding it.
  if (line.convertedFromPrimitive) {
    next.convertedFromPrimitive = {
      ...line.convertedFromPrimitive,
      primitive: rotatePrimitive(
        line.convertedFromPrimitive.primitive,
        deg,
        cx,
        cy,
      )!,
    };
  }
  if (trackRotation) {
    // Endpoint-based primitives (line / pill) used to clear
    // `rotation` / `rotationCenter` on the theory that the endpoints
    // already encoded orientation, but doing so wiped the bbox-tilt
    // invariant: `getUnrotatedBounds` fell through to the rotated
    // pathData's axis-aligned bounds, giving a fat diagonal square
    // around what should be a thin tilted line (issue #360). Track
    // rotation for every kind — the panel's angle field still reads
    // from the endpoints (same source of truth as before), and the
    // bbox overlay reads from `rotation` to draw the natural-form
    // shape tilted by the right angle.
    const oldDeg = line.rotation ?? 0;
    const oldCenter = line.rotationCenter;
    if (oldCenter && Math.abs(oldDeg) > 1e-6) {
      // Compose with the existing rotation so the bbox-tilt
      // invariant survives a pivot change (e.g., a bbox-centre
      // rotation followed by an anchor-pivot rotation). The
      // composed rotation maps the natural form to the new
      // displayed pathData about a single derived pivot.
      const composed = composeRotations(oldDeg, oldCenter, deg, {
        x: cx,
        y: cy,
      });
      if (composed) {
        next.rotation = composed.deg;
        next.rotationCenter = composed.center;
      } else {
        // Composition collapsed to a pure translation (sum angle =
        // 0 mod 360). No single rotation pivot — clear the field.
        if (next.rotation !== undefined) delete next.rotation;
        if (next.rotationCenter !== undefined) delete next.rotationCenter;
      }
    } else {
      next.rotation = normalizeRotation(deg);
      next.rotationCenter = { x: cx, y: cy };
    }
  } else {
    // Caller explicitly opts out of bbox-tilt tracking (e.g.,
    // group rotation tracks the angle at the group level, not on
    // each member). Bake into pathData and clear the field.
    if (next.rotation !== undefined) delete next.rotation;
    if (next.rotationCenter !== undefined) delete next.rotationCenter;
  }
  if (line.cornerRounding) {
    next.cornerRounding = {
      ...line.cornerRounding,
      sharpPathData: rotatePathData(
        line.cornerRounding.sharpPathData,
        cx,
        cy,
        deg,
        scope,
      ),
    };
  }
  // Freehand `rawPoints` is the source of truth the smoothness
  // derive pass reads on every commit (issue #171). Without rotating
  // it in lockstep, the derive would re-emit the un-rotated polyline
  // and clobber the rotated `pathData` we just produced — only the
  // bbox would appear rotated. Same shape as the `cornerRounding`
  // mirror above.
  if (line.rawPoints) {
    next.rawPoints = rotatePoints(line.rawPoints, cx, cy, deg);
  }
  return next;
}

function rotatePoints(
  pts: ReadonlyArray<{ x: number; y: number }>,
  cx: number,
  cy: number,
  deg: number,
): { x: number; y: number }[] {
  if (Math.abs(deg) < 1e-6) return pts.map((p) => ({ x: p.x, y: p.y }));
  const rad = (deg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  return pts.map((p) => {
    const dx = p.x - cx;
    const dy = p.y - cy;
    return { x: cx + dx * cos - dy * sin, y: cy + dx * sin + dy * cos };
  });
}

export function rotatePathData(
  d: string,
  cx: number,
  cy: number,
  deg: number,
  scope: paper.PaperScope,
): string {
  if (!d || Math.abs(deg) < 1e-6) return d;
  try {
    const cp = new scope.CompoundPath({ pathData: d, insert: false });
    cp.rotate(deg, new scope.Point(cx, cy));
    return cp.pathData;
  } catch {
    return d;
  }
}

/** Project-space un-rotated bbox of a construction GROUP — the
 *  union of every descendant line's bbox AFTER each line is un-
 *  rotated by `-group.rotation` about `group.rotationCenter`. The
 *  group overlay then renders this rectangle tilted by `+rotation`
 *  about the same pivot, so each rotation step rotates the original
 *  frame instead of spawning a fresh axis-aligned bbox around the
 *  rotated cluster. Returns null when the group has no rendered
 *  members yet. */
export function getUnrotatedGroupBounds(
  project: Project,
  group: ConstructionGroup,
  scope: paper.PaperScope,
): {
  x: number;
  y: number;
  width: number;
  height: number;
  cx: number;
  cy: number;
} | null {
  const lineIds = descendantConstructionLineIds(project, group.id);
  const imageIds = descendantConstructionImageIds(project, group.id);
  if (lineIds.length === 0 && imageIds.length === 0) return null;
  const rot = group.rotation ?? 0;
  let cx = 0;
  let cy = 0;
  if (group.rotationCenter) {
    cx = group.rotationCenter.x;
    cy = group.rotationCenter.y;
  }
  let unionMinX = Infinity;
  let unionMinY = Infinity;
  let unionMaxX = -Infinity;
  let unionMaxY = -Infinity;
  if (lineIds.length > 0) {
    const center = new scope.Point(cx, cy);
    for (const lid of lineIds) {
      const line = projectConstructionLines(project).find((l) => l.id === lid);
      if (!line) continue;
      let path: paper.PathItem;
      try {
        path = new scope.CompoundPath({
          pathData: line.pathData,
          insert: false,
        });
      } catch {
        continue;
      }
      if (Math.abs(rot) > 1e-6) {
        path.rotate(-rot, center);
      }
      const b = path.bounds;
      if (b.x < unionMinX) unionMinX = b.x;
      if (b.y < unionMinY) unionMinY = b.y;
      if (b.x + b.width > unionMaxX) unionMaxX = b.x + b.width;
      if (b.y + b.height > unionMaxY) unionMaxY = b.y + b.height;
    }
  }
  // Member tracing images (issue #509 / #545) contribute their footprint
  // to the un-rotated bbox so it (and the rotation-handle hit-test derived
  // from it) encloses images, not just lines. The image rotated *with* the
  // group, so in the group's un-rotated frame its centre un-orbits by
  // `-rot` about the pivot and its effective tilt is (own rotation − group
  // rotation) — zero for an image that was upright before the group
  // turned, which keeps the box tight to the rectangle instead of the
  // inflated AABB of a tilted rect.
  if (imageIds.length > 0) {
    const imageSet = new Set(imageIds);
    const rad = (-rot * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    for (const img of projectTracingImages(project)) {
      if (!imageSet.has(img.id)) continue;
      const icx = img.x + img.widthMm / 2;
      const icy = img.y + img.heightMm / 2;
      let ucx = icx;
      let ucy = icy;
      if (Math.abs(rot) > 1e-6) {
        ucx = cx + (icx - cx) * cos - (icy - cy) * sin;
        ucy = cy + (icx - cx) * sin + (icy - cy) * cos;
      }
      const relRad = ((tracingImageRotation(img) - rot) * Math.PI) / 180;
      const rc = Math.cos(relRad);
      const rs = Math.sin(relRad);
      const hw = img.widthMm / 2;
      const hh = img.heightMm / 2;
      const offs: ReadonlyArray<readonly [number, number]> = [
        [-hw, -hh],
        [hw, -hh],
        [hw, hh],
        [-hw, hh],
      ];
      for (const [ox, oy] of offs) {
        const qx = ucx + ox * rc - oy * rs;
        const qy = ucy + ox * rs + oy * rc;
        if (qx < unionMinX) unionMinX = qx;
        if (qy < unionMinY) unionMinY = qy;
        if (qx > unionMaxX) unionMaxX = qx;
        if (qy > unionMaxY) unionMaxY = qy;
      }
    }
  }
  if (!Number.isFinite(unionMinX)) return null;
  // Fallback pivot when none is stored: centre of the un-rotated
  // union (which is just the axis-aligned union of members, since
  // rotation is 0). Stays stable for follow-up gestures.
  if (!group.rotationCenter) {
    cx = (unionMinX + unionMaxX) / 2;
    cy = (unionMinY + unionMaxY) / 2;
  }
  return {
    x: unionMinX,
    y: unionMinY,
    width: unionMaxX - unionMinX,
    height: unionMaxY - unionMinY,
    cx,
    cy,
  };
}

// Suppress unused-import warning (re-exported types may not all be
// referenced inside this file but live with the rotation helpers).
void projectConstructionGroups;

/** Project-space un-rotated bbox of a construction line — i.e., the
 *  bbox the line would have if its `rotation` were 0. Used by the
 *  selection overlay to draw a tilted rectangle whose dimensions
 *  match the natural orientation (so the bbox doesn't grow on each
 *  rotation step). Falls back to the rendered pathData bbox for
 *  lines that carry no rotation metadata yet. */
/** Resolve a constraint ref to its world-space point on the line's
 *  CURRENT pathData (pre-rotation). Returns null for refs that
 *  don't reduce to a single point (whole-line refs, curve refs). */
export function resolveRefPoint(
  ref: ConstraintRef,
  line: ConstructionLine,
  scope: paper.PaperScope,
): { x: number; y: number } | null {
  if (ref.kind === "line") return null;
  if (ref.kind === "curve") return null;
  let cp: paper.CompoundPath;
  try {
    cp = new scope.CompoundPath({ pathData: line.pathData, insert: false });
  } catch {
    return null;
  }
  if (ref.kind === "anchor") {
    const subs: paper.Path[] =
      cp.children && cp.children.length > 0
        ? (cp.children as paper.Path[])
        : [cp as unknown as paper.Path];
    const sub = subs[ref.pathIndex];
    if (!sub) return null;
    const seg = sub.segments[ref.segmentIndex];
    if (!seg) return null;
    return { x: seg.point.x, y: seg.point.y };
  }
  if (ref.kind === "center") {
    const b = cp.bounds;
    return { x: b.x + b.width / 2, y: b.y + b.height / 2 };
  }
  if (ref.kind === "bbox-midpoint") {
    const b = cp.bounds;
    switch (ref.edge) {
      case "top":
        return { x: b.x + b.width / 2, y: b.y };
      case "bottom":
        return { x: b.x + b.width / 2, y: b.y + b.height };
      case "left":
        return { x: b.x, y: b.y + b.height / 2 };
      case "right":
        return { x: b.x + b.width, y: b.y + b.height / 2 };
    }
  }
  return null;
}

/** For a rotation of `rotatedLineIds` by `deg` about `(cx, cy)`,
 *  compute the translation delta each line in the snap chain needs
 *  so its pinned anchor stays coincident with whatever it was
 *  pinned to.
 *
 *  User-facing rule (issue #67 follow-up): partners with snap
 *  constraints to a rotating line translate — not rotate — to keep
 *  their pinned anchor in place. Chain propagation (BFS through
 *  the snap-edge graph) lets a transitive partner like
 *  `A→B→C→D` translate by the same delta as its upstream neighbour
 *  so the whole chain follows the rotation. Each line's offset
 *  descendants inherit the same delta because the offset
 *  relationship is rigid under translation.
 *
 *  Result excludes the initial rotated lines and their offset
 *  descendants — those are handled by the rotation pathway. */
export function rotationPartnerTranslations(
  project: Project,
  rotatedLineIds: ReadonlySet<string>,
  deg: number,
  cx: number,
  cy: number,
  scope: paper.PaperScope,
): Map<string, { dx: number; dy: number }> {
  const result = new Map<string, { dx: number; dy: number }>();
  if (Math.abs(deg) < 1e-6 || rotatedLineIds.size === 0) return result;

  const linesById = new Map<string, ConstructionLine>();
  for (const l of projectConstructionLines(project)) linesById.set(l.id, l);

  // Offset graph (source → direct descendants), used both for
  // expanding the rotated set and for inheriting partner deltas
  // onto offset descendants.
  const offsetChildren = new Map<string, string[]>();
  for (const l of projectConstructionLines(project)) {
    const s = l.offsetSource?.sourceId;
    if (!s) continue;
    let arr = offsetChildren.get(s);
    if (!arr) {
      arr = [];
      offsetChildren.set(s, arr);
    }
    arr.push(l.id);
  }

  const expandWithOffsets = (id: string, sink: Set<string>): string[] => {
    const added: string[] = [];
    const queue: string[] = [id];
    while (queue.length > 0) {
      const cur = queue.shift()!;
      if (sink.has(cur)) continue;
      sink.add(cur);
      added.push(cur);
      for (const child of offsetChildren.get(cur) ?? []) queue.push(child);
    }
    return added;
  };

  // Rotated closure = initial rotated lines + every (transitive)
  // offset descendant. These rotate rigidly with the source so they
  // never appear in the translation result map.
  const rotated = new Set<string>();
  for (const id of rotatedLineIds) expandWithOffsets(id, rotated);

  // Snap adjacency: for each line, a list of (refOnThisLine,
  // refOnOtherLine) edges drawn from every coincident constraint
  // the line participates in.
  type Edge = { thisRef: ConstraintRef; otherRef: ConstraintRef };
  const snapAdj = new Map<string, Edge[]>();
  for (const c of projectConstraints(project)) {
    if (c.kind !== "coincident") continue;
    for (const ref of c.refs) {
      for (const otherRef of c.refs) {
        if (ref.constructionId === otherRef.constructionId) continue;
        let arr = snapAdj.get(ref.constructionId);
        if (!arr) {
          arr = [];
          snapAdj.set(ref.constructionId, arr);
        }
        arr.push({ thisRef: ref, otherRef });
      }
    }
  }

  // BFS: each visited line either rotates (in `rotated` set) or
  // translates (delta in `result`). For each snap edge from a
  // visited line to an un-visited line, compute the anchor's
  // post-transform position, derive the partner's translation, and
  // enqueue the partner + its offset descendants with that delta.
  const visited = new Set<string>(rotated);
  const queue: string[] = [...rotated];
  while (queue.length > 0) {
    const cur = queue.shift()!;
    const curLine = linesById.get(cur);
    if (!curLine) continue;
    const edges = snapAdj.get(cur) ?? [];
    for (const edge of edges) {
      const otherId = edge.otherRef.constructionId;
      if (visited.has(otherId)) continue;
      const anchorBefore = resolveRefPoint(edge.thisRef, curLine, scope);
      if (!anchorBefore) continue;

      let anchorAfter: { x: number; y: number };
      if (rotated.has(cur)) {
        const rad = (deg * Math.PI) / 180;
        const cosA = Math.cos(rad);
        const sinA = Math.sin(rad);
        const dxC = anchorBefore.x - cx;
        const dyC = anchorBefore.y - cy;
        anchorAfter = {
          x: cx + dxC * cosA - dyC * sinA,
          y: cy + dxC * sinA + dyC * cosA,
        };
      } else {
        const tDelta = result.get(cur);
        if (!tDelta) continue;
        anchorAfter = {
          x: anchorBefore.x + tDelta.dx,
          y: anchorBefore.y + tDelta.dy,
        };
      }
      const dx = anchorAfter.x - anchorBefore.x;
      const dy = anchorAfter.y - anchorBefore.y;

      // Mark the partner AND every (transitive) offset descendant
      // with the same delta — translation propagates rigidly
      // through the offset graph.
      const fresh = expandWithOffsets(otherId, visited);
      for (const id of fresh) {
        result.set(id, { dx, dy });
        queue.push(id);
      }
    }
  }

  return result;
}

export function getUnrotatedBounds(
  line: ConstructionLine,
  scope: paper.PaperScope,
): {
  x: number;
  y: number;
  width: number;
  height: number;
  cx: number;
  cy: number;
} | null {
  let path: paper.PathItem;
  try {
    path = new scope.CompoundPath({ pathData: line.pathData, insert: false });
  } catch {
    return null;
  }
  const rot = line.rotation ?? 0;
  const center: Vec2 = line.rotationCenter ?? {
    x: path.bounds.x + path.bounds.width / 2,
    y: path.bounds.y + path.bounds.height / 2,
  };
  if (Math.abs(rot) > 1e-6) {
    path.rotate(-rot, new scope.Point(center.x, center.y));
  }
  const b = path.bounds;
  return {
    x: b.x,
    y: b.y,
    width: b.width,
    height: b.height,
    cx: center.x,
    cy: center.y,
  };
}

/** Map a world-space point into the line's un-rotated reference frame
 *  so axis-aligned bbox checks just work. Inverse of "render the bbox
 *  tilted by `line.rotation` about `line.rotationCenter`". Shared by the
 *  rotation hit-testing (RotationController) and the corner-resize
 *  hit-testing (ConstructionTool). */
export function worldToLineLocal(
  line: ConstructionLine,
  p: { x: number; y: number },
): { x: number; y: number } {
  const center = line.rotationCenter;
  if (!center) return { x: p.x, y: p.y };
  return unrotatePoint(p, center, line.rotation ?? 0);
}

/** The DISPLAYED bbox centre of a rotated shape: the un-rotated bbox
 *  centre `(x + w/2, y + h/2)` rotated by `rotationDeg` about the
 *  bounds' stored pivot `(cx, cy)`. This is what the user perceives
 *  as "the middle of the tilted box" and is the natural default
 *  rotation pivot when no custom one is set (issue #420). */
export function displayedBboxCenter(
  bounds: {
    x: number;
    y: number;
    width: number;
    height: number;
    cx: number;
    cy: number;
  },
  rotationDeg: number,
): Vec2 {
  return displayedBoxCenter(
    bounds,
    Math.abs(rotationDeg) < 1e-6
      ? null
      : { deg: rotationDeg, center: { x: bounds.cx, y: bounds.cy } },
  );
}

/** Effective rotation pivot for a construction line (issue #420):
 *  the user-positioned `pivotPoint` when present, else the displayed
 *  bbox centre. Returns null when the line's geometry can't be
 *  resolved (un-parseable pathData). */
export function effectiveLinePivot(
  line: ConstructionLine,
  scope: paper.PaperScope,
): Vec2 | null {
  if (line.pivotPoint) return { x: line.pivotPoint.x, y: line.pivotPoint.y };
  const bounds = getUnrotatedBounds(line, scope);
  if (!bounds) return null;
  return displayedBboxCenter(bounds, line.rotation ?? 0);
}

/** Effective rotation pivot for a construction group (issue #420):
 *  the user-positioned `pivotPoint` when present, else the displayed
 *  union-bbox centre. Returns null when the group has no resolvable
 *  member geometry. */
export function effectiveGroupPivot(
  project: Project,
  group: ConstructionGroup,
  scope: paper.PaperScope,
): Vec2 | null {
  if (group.pivotPoint) return { x: group.pivotPoint.x, y: group.pivotPoint.y };
  const bounds = getUnrotatedGroupBounds(project, group, scope);
  if (!bounds) return null;
  return displayedBboxCenter(bounds, group.rotation ?? 0);
}

/** Effective rotation pivot for a tracing image (issue #420 extended to
 *  images under #545): the user-positioned `pivotPoint` when present, else
 *  the image's centre. No Paper scope needed — the image centre is
 *  closed-form, unlike a line's parsed-pathData bbox. */
export function effectiveImagePivot(image: TracingImage): Vec2 {
  if (image.pivotPoint) return { x: image.pivotPoint.x, y: image.pivotPoint.y };
  return tracingImageCenter(image);
}

/** Apply a snap-partner translation to one construction line — same
 *  pathData / primitive / corner-rounding / rotation-centre updates
 *  the regular per-line translate does, but inlined here so the
 *  rotation commit can use a per-line delta (rather than the single
 *  delta the closure-walk assumes). Keeps the partner's `rotation`
 *  field untouched — the partner translates, it doesn't rotate. */
export function translateLineForRotationPartner(
  line: ConstructionLine,
  dx: number,
  dy: number,
  scope: paper.PaperScope,
): ConstructionLine {
  if (Math.abs(dx) < 1e-6 && Math.abs(dy) < 1e-6) return line;
  return translateConstructionLine(line, dx, dy, scope);
}
